import * as React from 'react';
import { ArrowUpRight, Check, ThumbsDown, ThumbsUp } from 'lucide-react';
import type {
  GrowthFeedback,
  GrowthOpportunity,
} from '@shared/api-client/growth';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Alert } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { textAreaClass } from './plan-form';

const statuses = {
  new: 'Suggested',
  watching: 'Watching',
  trying: 'Trying this',
  dismissed: 'Dismissed',
  done: 'Reviewed',
};
const dateLabel = (date: string) =>
  new Date(date).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  });

export const Opportunity = ({
  item,
  canManage,
  saving,
  error,
  onSave,
}: {
  item: GrowthOpportunity;
  canManage: boolean;
  saving: boolean;
  error?: string;
  onSave: (id: string, feedback: GrowthFeedback) => void;
}) => {
  const sourceCount = new Set(
    item.citations.map((citation) => citation.sourceId),
  ).size;
  const [editing, setEditing] = React.useState(false);
  const [note, setNote] = React.useState(item.note);
  const [reviewAt, setReviewAt] = React.useState(
    item.reviewAt?.slice(0, 10) ?? '',
  );
  const due =
    item.status === 'trying' &&
    item.reviewAt &&
    new Date(item.reviewAt).getTime() <= Date.now();
  const save = (changes: Partial<GrowthFeedback>) =>
    onSave(item.id, {
      status: item.status,
      useful: item.useful,
      note: item.note,
      reviewAt: item.reviewAt,
      ...changes,
    });
  return (
    <article className="min-w-0 rounded-xl border border-border bg-surface p-5 sm:p-7">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
        <span
          className={cn('font-medium', due ? 'text-warning' : 'text-accent')}
        >
          {due ? 'Ready for your review' : statuses[item.status]}
        </span>
        <span>{dateLabel(item.createdAt)}</span>
        <span>
          {item.confidence === 'medium'
            ? 'Supported hypothesis'
            : 'Early signal'}
        </span>
      </div>
      <h2 className="mt-3 max-w-3xl text-lg font-semibold leading-snug text-ink sm:text-xl">
        {item.title}
      </h2>
      <p className="mt-3 max-w-3xl text-sm leading-relaxed text-ink-muted">
        {item.observation}
      </p>
      <p className="mt-3 max-w-3xl text-sm leading-relaxed text-ink">
        <span className="font-medium">Why it matters. </span>
        {item.relevance}
      </p>
      <div className="mt-5 grid gap-4 border-y border-border py-5 md:grid-cols-[1.35fr_1fr]">
        <div>
          <h3 className="text-sm font-semibold text-ink">A small next step</h3>
          <p className="mt-1.5 text-sm leading-relaxed text-ink-muted">
            {item.action}
          </p>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-ink">What to measure</h3>
          <p className="mt-1.5 text-sm leading-relaxed text-ink-muted">
            {item.successMeasure}
          </p>
        </div>
      </div>
      <p className="mt-4 text-sm leading-relaxed text-ink-muted">
        <span className="font-medium text-ink">What we don’t know. </span>
        {item.uncertainty}
      </p>
      <details className="mt-4 text-sm">
        <summary className="w-fit cursor-pointer font-medium text-ink">
          Evidence · {sourceCount} {sourceCount === 1 ? 'source' : 'sources'}
        </summary>
        <ol className="mt-3 space-y-4">
          {item.citations.map((citation) => (
            <li
              key={`${citation.sourceId}:${citation.quote}`}
              className="min-w-0"
            >
              <a
                className="inline-flex max-w-full items-start gap-1 break-words text-accent underline underline-offset-4"
                href={citation.url}
                target={citation.url.startsWith('/') ? undefined : '_blank'}
                rel="noreferrer"
              >
                <span>{citation.title}</span>
                <ArrowUpRight className="mt-0.5 size-3.5 shrink-0" />
              </a>
              <blockquote className="mt-1 max-w-3xl break-words text-sm leading-relaxed text-ink-muted">
                “{citation.quote}”
              </blockquote>
              <p className="mt-1 text-xs text-ink-muted">
                {citation.publishedAt
                  ? `Published ${dateLabel(citation.publishedAt)} · `
                  : 'Publication date unknown · '}
                Observed {dateLabel(citation.observedAt)}
              </p>
            </li>
          ))}
        </ol>
      </details>
      {item.reviewAt && item.status === 'trying' && (
        <p className="mt-4 text-sm text-ink-muted">
          Review on {dateLabel(item.reviewAt)}. Record what you changed and what
          happened; GainSignal won’t assume the result.
        </p>
      )}
      {item.note && !editing && (
        <p className="mt-4 whitespace-pre-wrap text-sm text-ink-muted">
          <span className="font-medium text-ink">Your notes: </span>
          {item.note}
        </p>
      )}
      {error && (
        <div className="mt-4">
          <Alert tone="error">{error}</Alert>
        </div>
      )}
      {canManage && (
        <div className="mt-5 flex flex-wrap items-center gap-2">
          {item.status !== 'trying' && item.status !== 'done' && (
            <Button
              size="sm"
              loading={saving}
              onClick={() => save({ status: 'trying', reviewAt: null })}
            >
              Try this
            </Button>
          )}
          {item.status === 'trying' && (
            <Button
              size="sm"
              variant="secondary"
              disabled={saving}
              onClick={() => setEditing(true)}
            >
              Record outcome
            </Button>
          )}
          {item.status !== 'watching' && item.status !== 'done' && (
            <Button
              size="sm"
              variant="ghost"
              disabled={saving}
              onClick={() => save({ status: 'watching', reviewAt: null })}
            >
              Keep watching
            </Button>
          )}
          {item.status !== 'dismissed' && (
            <Button
              size="sm"
              variant="ghost"
              disabled={saving}
              onClick={() => save({ status: 'dismissed' })}
            >
              Dismiss
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            disabled={saving}
            onClick={() => setEditing(!editing)}
          >
            Notes
          </Button>
          <div className="ml-auto flex items-center gap-1">
            <span className="mr-1 text-xs text-ink-muted">Useful?</span>
            <Button
              size="icon"
              variant={item.useful === true ? 'secondary' : 'ghost'}
              disabled={saving}
              aria-label="Mark useful"
              aria-pressed={item.useful === true}
              onClick={() =>
                save({ useful: item.useful === true ? null : true })
              }
            >
              <ThumbsUp />
            </Button>
            <Button
              size="icon"
              variant={item.useful === false ? 'secondary' : 'ghost'}
              disabled={saving}
              aria-label="Mark not useful"
              aria-pressed={item.useful === false}
              onClick={() =>
                save({ useful: item.useful === false ? null : false })
              }
            >
              <ThumbsDown />
            </Button>
          </div>
        </div>
      )}
      {editing && (
        <form
          className="mt-5 space-y-4 border-t border-border pt-5"
          onSubmit={(event) => {
            event.preventDefault();
            save({
              note,
              reviewAt: reviewAt
                ? new Date(`${reviewAt}T12:00:00`).toISOString()
                : null,
            });
            setEditing(false);
          }}
        >
          <Field label="What did you try or learn?" htmlFor={`note-${item.id}`}>
            <textarea
              className={textAreaClass}
              rows={3}
              maxLength={2000}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </Field>
          <Field label="Review date" htmlFor={`review-${item.id}`}>
            <Input
              className="max-w-56"
              type="date"
              value={reviewAt}
              onChange={(event) => setReviewAt(event.target.value)}
            />
          </Field>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm" loading={saving}>
              Save notes
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={saving}
              onClick={() => {
                save({ status: 'done', note, reviewAt: null });
                setEditing(false);
              }}
            >
              <Check />
              Mark reviewed
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </article>
  );
};
